import type { AgentPatch } from '@tunasong/agent-tools'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './proposal-item.module.css'
import { isDestructiveProposal, type ProposalComment } from './proposal-resolve.js'

export interface ProposalItemProps {
  proposal: ProposalComment
  onAccept: () => void
  onReject: () => void
  /**
   * When true, omits the "in {entity.name}" line. Use in surfaces that are
   * already scoped to the target entity (gutter chip, in-panel header). The
   * standalone proposal-list Popover keeps the line so the user knows the
   * proposal targets the current entity vs. a sibling.
   */
  hideTargetLabel?: boolean
  /** When true, the destructive verb gets red color + autoFocus on Reject. */
  destructive?: boolean
  /**
   * When set, an inline error line is shown below the actions. Wired by the
   * parent from `acceptProposal`'s `{ ok: false, reason }` result so a failed
   * Accept reports WHY instead of silently doing nothing.
   */
  error?: string
  className?: string
}

/**
 * One proposal rendered as: summary line, optional "in {entity}" label,
 * patch preview, Accept/Reject buttons. Shared between the standalone
 * proposal-list Popover, the gutter chip popover, and the side-panel
 * "Proposals (n)" header.
 *
 * Accept/Reject paths are owned by the caller via `proposal-resolve` —
 * this component is presentation only; the resolve module's
 * acceptProposal / rejectProposal helpers are wired by the parent.
 */
export const ProposalItem: FC<ProposalItemProps> = ({
  proposal,
  onAccept,
  onReject,
  hideTargetLabel = false,
  destructive: destructiveProp,
  error,
  className,
}) => {
  const destructive = destructiveProp ?? isDestructiveProposal(proposal)
  const { entity: target } = graphHooks.useEntity<Persisted<Entity>>(
    hideTargetLabel ? undefined : proposal.metadata?.targetEntityId
  )
  const summary = proposal.metadata?.summary ?? 'Proposed edit'
  return (
    <div className={[styles.item, className].filter(Boolean).join(' ')} role="listitem">
      <div className={styles.summary}>
        <strong className={destructive ? styles['summary--destructive'] : undefined}>{summary}</strong>
      </div>
      {!hideTargetLabel && target?.name ? (
        <div className={styles.target}>
          <small>in {target.name}</small>
        </div>
      ) : null}
      <ProposalPreview patch={proposal.metadata?.patch} />
      <div className={styles.actions}>
        <Button variant="contained" color="primary" size="small" onClick={onAccept}>
          Accept
        </Button>
        <Button
          variant="text"
          size="small"
          color={destructive ? 'error' : undefined}
          autoFocus={destructive}
          onClick={onReject}
        >
          Reject
        </Button>
      </div>
      {error ? (
        <div className={styles.error} role="alert">
          <small>{`Couldn't apply: ${error}`}</small>
        </div>
      ) : null}
    </div>
  )
}

const ProposalPreview: FC<{ patch?: AgentPatch }> = ({ patch }) => {
  if (!patch) {
    return null
  }
  if (patch.kind === 'slate-ops') {
    return <small>{`${patch.ops.length} editor operation${patch.ops.length === 1 ? '' : 's'}`}</small>
  }
  if (patch.kind === 'entity-metadata') {
    const fieldNames = Object.keys(patch.fields).join(', ')
    return <small>{`update ${fieldNames}`}</small>
  }
  if (patch.kind === 'editor-command') {
    return <small>{`${patch.commandId} @ ${patch.anchor.position} ${patch.anchor.id}`}</small>
  }
  const exhaustive: never = patch
  return <small>{`unknown patch kind: ${JSON.stringify(exhaustive)}`}</small>
}
